import { logError } from "services/logService/errorLogger";

const ZERO_DECIMAL_CURRENCIES = new Set([
  'bif', 'clp', 'djf', 'gnf', 'jpy', 'kmf', 'krw', 'mga', 'pyg', 'rwf', 'ugx', 'vnd', 'vuv', 'xaf', 'xof', 'xpf'
]);

export function getCurrencyDivisor(currency: string): number {
    return ZERO_DECIMAL_CURRENCIES.has(currency.toLowerCase()) ? 1 : 100;
}

/**
 * Formats a given amount in cents to a currency string.
 * @param cents - The amount in cents.
 * @param currency - The currency code (e.g., "USD", "KES").
 * @returns The formatted currency string.
 */
export function formatCentsToCurrency({ cents, currency }: { cents: number; currency: string }): string {
    const divisor = getCurrencyDivisor(currency);
    const amount = cents / divisor;

    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency.toUpperCase(),
        }).format(amount);
    } catch (error) {
        logError(
            "Error formatting currency",
            { currency, amount, error },
            'formatCurrency.ts'
        );
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: divisor === 1 ? 0 : 2,
            maximumFractionDigits: divisor === 1 ? 0 : 2,
        }).format(amount);
    }
}

/**
 * Formats a given amount with the specified currency.
 * If amount is invalid or zero, it returns a fallback message.
 * 
 * @param {string} currency - The currency code (e.g., "USD", "KES").
 * @param {number} amount - The amount to be formatted.
 * @returns {string} - Formatted currency string or a fallback message.
 */
export const formatCurrency = (currency: string, amount: number): string => {
    try {
        // If the amount is zero or NaN, return the fallback message
        if (!amount || amount <= 0) {
            return "Contact us"
        }

        // Format the currency with the provided currency code
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    } catch (error) {
        logError(
            "Error formatting currency",
            { currency, amount, error },
            'formatCurrency.ts'
        );
        // Return fallback message if any error occurs
        return "Contact us"
    }
}